export type AdaptivePollingOptions = {
    intervalMs: number;
    maxBackoffMs?: number;
    isActive?: () => boolean;
    random?: () => number;
};

export type AdaptivePoller = {
    start: () => void;
    stop: () => void;
    trigger: () => void;
};

export function createAdaptivePoller(
    task: (controller: AbortController) => Promise<void>,
    options: AdaptivePollingOptions
): AdaptivePoller {
    const maxBackoffMs = options.maxBackoffMs ?? Math.max(options.intervalMs, 60_000);
    const isActive = options.isActive ?? (() => true);
    const random = options.random ?? Math.random;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = true;
    let running = false;
    let consecutiveFailures = 0;
    let activeController: AbortController | undefined;

    const clearTimer = () => {
        if (timer !== undefined) {
            clearTimeout(timer);
            timer = undefined;
        }
    };

    const schedule = (delayMs: number) => {
        clearTimer();
        if (!stopped) {
            timer = setTimeout(run, delayMs);
        }
    };

    const nextDelay = () => {
        const backoff = Math.min(maxBackoffMs, options.intervalMs * 2 ** consecutiveFailures);
        return Math.min(maxBackoffMs, backoff + Math.floor(backoff * 0.1 * random()));
    };

    async function run() {
        timer = undefined;
        if (stopped || running) return;

        if (!isActive()) {
            schedule(options.intervalMs);
            return;
        }

        running = true;
        activeController = new AbortController();
        try {
            await task(activeController);
            consecutiveFailures = 0;
        } catch {
            consecutiveFailures += 1;
        } finally {
            activeController = undefined;
            running = false;
            schedule(nextDelay());
        }
    }

    return {
        start() {
            if (!stopped) return;
            stopped = false;
            schedule(0);
        },
        stop() {
            stopped = true;
            clearTimer();
            activeController?.abort();
        },
        trigger() {
            if (stopped || running) return;
            schedule(0);
        }
    };
}
